import React, { useEffect, useState } from "react";
import { Modal, Checkbox } from "antd";
import styles from "./pptStatusLifecycle.module.scss";

/**
 * Confirmation modal that always opens when the user clicks Complete Stage.
 * Three modes drive the title, body, and footer:
 *
 *   - "clean"    — no issues. Simple confirmation. Cancel + Advance.
 *   - "warnings" — list of warning discounts + required ack checkbox before
 *                  the Advance button enables.
 *   - "blocked"  — read-only list of blocking issues. No advance button;
 *                  Close only.
 *
 * Once per-rule severity matrices land in the backend we'll be able to render
 * "WARNING at <stage> → ERROR at <next>" badges per issue (matching the
 * design mockup). For now the per-discount list is what we can honestly show.
 */
export const AdvanceModal = ({
  open,
  mode, // "clean" | "warnings" | "blocked"
  currentStageName,
  nextStageName, // null when completing the last stage
  errorDiscounts,
  warningDiscounts,
  onCancel,
  onConfirm,
  isConfirming,
}) => {
  const [acked, setAcked] = useState(false);

  useEffect(() => {
    if (open) setAcked(false);
  }, [open]);

  const isBlocked = mode === "blocked";
  const isClean = mode === "clean";
  const isFinal = !nextStageName;

  const warningCount = warningDiscounts?.length ?? 0;

  const title = (() => {
    if (isBlocked) {
      return isFinal
        ? "Cannot mark as done — issues block the gate"
        : "Cannot advance — issues block the gate";
    }
    if (mode === "warnings") {
      return isFinal
        ? `Mark as done with ${warningCount} warning${warningCount === 1 ? "" : "s"}?`
        : `Advance to ${nextStageName} with ${warningCount} warning${warningCount === 1 ? "" : "s"}?`;
    }
    // clean
    return isFinal
      ? "Mark this promotion as done?"
      : `Advance to ${nextStageName}?`;
  })();

  const subtitle = (() => {
    if (isFinal) {
      return "All stages will be marked complete and the promotion's lifecycle will be finished.";
    }
  })();

  const okText = isFinal ? "Mark as Done" : "Advance";
  const okWithWarningsText = isFinal
    ? "Mark as Done with warnings"
    : "Advance with warnings";

  const rows =
    mode === "warnings"
      ? warningDiscounts.map((d) => ({ ...d, _severity: "WARNING" }))
      : isBlocked
        ? [
            ...errorDiscounts.map((d) => ({ ...d, _severity: "ERROR" })),
            ...warningDiscounts.map((d) => ({ ...d, _severity: "WARNING" })),
          ]
        : [];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      okText={mode === "warnings" ? okWithWarningsText : okText}
      okButtonProps={{
        disabled: isBlocked || (mode === "warnings" && !acked),
        loading: isConfirming,
        style: isBlocked ? { display: "none" } : undefined,
      }}
      cancelText={isBlocked ? "Close" : "Cancel"}
      onOk={onConfirm}
      destroyOnClose
      // Per the design briefs: "Modals are centered, dismiss on backdrop
      // click, and surface inline help."
      centered
    >
      <div className={styles.modalBody}>
        {!isClean && <p className={styles.modalSubtitle}>{subtitle}</p>}

        {isClean ? (
          <p className={styles.modalSubtitle}>
            {isFinal ? (
              "Confirm to finish the lifecycle."
            ) : (
              <>
                Confirm to mark <strong>{currentStageName}</strong> complete and
                advance to <strong>{nextStageName}</strong>.
              </>
            )}
          </p>
        ) : (
          rows.length > 0 && (
            <ul className={styles.issueList}>
              {rows.map((d) => (
                <li key={d.id} className={styles.issueItem}>
                  <span
                    className={`${styles.issueSeverity} ${
                      d._severity === "ERROR"
                        ? styles.issueSeverityError
                        : styles.issueSeverityWarning
                    }`}
                  >
                    {d._severity}
                  </span>
                  <span className={styles.issueDescription}>
                    {d.title || "(untitled discount)"}
                    {d.format ? ` — ${d.format}` : ""}
                    {d.territory ? ` · ${d.territory}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )
        )}

        {isBlocked && (
          <div className={`${styles.modalCallout} ${styles.calloutBlocked}`}>
            Resolve these issues (or disable the affected discounts) before this
            stage can advance. Open a discount from the grid below to triage.
          </div>
        )}

        {mode === "warnings" && (
          <label className={styles.ackRow}>
            <Checkbox
              checked={acked}
              onChange={(e) => setAcked(e.target.checked)}
            />
            I acknowledge these warnings will carry forward.
          </label>
        )}
      </div>
    </Modal>
  );
};

// const gateAffix = (mode) => {
//   switch (mode) {
//     case "warnings":
//       return "allows warnings with acknowledgment, blocks on errors. Carried-forward issues will be re-evaluated under the next stage's rules";
//     case "blocked":
//       return "doesn't allow the current open issues";
//     default:
//       return "is clean";
//   }
// };
